import { Loading } from "../../app/Status";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { mentionPath, type MentionDir, type MentionDto, type MentionListResponse } from "@klantpunt/shared";
import { Segmented } from "../../app/Choice";
import { api } from "../../lib/api";
import { mentionsKey } from "./Logbook";
import { MentionText } from "./MentionText";

const fmt = (iso: string) => new Date(iso).toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" });

/** Vermeldingen aan en door mij; elke minuut ververst (ook voor de teller in het menu). */
export function useMentions(enabled = true) {
  return useQuery({
    queryKey: mentionsKey,
    queryFn: () => api<MentionListResponse>("/mentions"),
    enabled,
    refetchInterval: 60_000,
  });
}

const SOURCE_LABEL: Record<MentionDto["source"], string> = {
  issue: "Bevinding",
  intake: "Melding",
  company: "Bedrijf",
  person: "Persoon",
  document: "Document",
};

const names = (m: MentionDto) => [m.to, ...m.others].map((p) => p.name).join(", ");

/** Mijn taken › Vermeldingen: waar ik genoemd ben en waar ik iemand noemde (issue #154). */
export function MentionsList({ dir, onDir }: { dir: MentionDir; onDir: (d: MentionDir) => void }) {
  const q = useMentions();
  const qc = useQueryClient();
  const read = async (ids?: string[]) => {
    await api("/mentions/read", { method: "POST", body: ids ? { ids } : {} });
    await qc.invalidateQueries({ queryKey: mentionsKey });
  };
  const items = (q.data?.items ?? []).filter((m) => (dir === "aan" ? m.toMe : dir === "door" ? !m.toMe : true));
  const unread = q.data?.unread ?? 0;
  return (
    <>
      <div className="mentions-tools">
        <Segmented<MentionDir>
          label="Welke vermeldingen"
          value={dir}
          onChange={onDir}
          items={[
            { value: "alle", label: "Alles" },
            { value: "aan", label: "Aan mij" },
            { value: "door", label: "Door mij" },
          ]}
        />
        {unread > 0 && (
          <button type="button" className="btn small" onClick={() => void read()}>
            Alles gelezen
          </button>
        )}
      </div>
      {q.isPending && <Loading />}
      {q.isError && (
        <div className="alert" role="alert">
          Vermeldingen konden niet worden geladen.
        </div>
      )}
      {q.data && items.length === 0 && (
        <div className="panel">
          <p className="muted">
            {dir === "door"
              ? "Je hebt nog niemand genoemd. Typ @ in een logboek om een collega te noemen."
              : "Nog geen vermeldingen. Als iemand je met @ noemt in een logboek, zie je dat hier."}
          </p>
        </div>
      )}
      <ul className="mentions">
        {items.map((m) => {
          const unreadItem = m.toMe && !m.readAt;
          return (
            <li key={m.id} className={`panel mention-item${unreadItem ? " unread" : ""}`}>
              <div className="mention-head">
                {unreadItem && <span className="dot" aria-label="Ongelezen" />}
                <span className="grow">
                  {m.toMe ? (
                    <>
                      <strong>{m.by?.name ?? "Iemand"}</strong> noemde jou
                    </>
                  ) : (
                    <>
                      Jij noemde <strong>{names(m)}</strong>
                    </>
                  )}
                </span>
                <span className="muted small">{fmt(m.createdAt)}</span>
              </div>
              <Link
                to={mentionPath(m)}
                className="mention-target"
                onClick={() => {
                  if (unreadItem) void read([m.id]);
                }}
              >
                <span className="chip">{SOURCE_LABEL[m.source]}</span>
                <span className="code small">{m.code}</span>
                <span className="grow">{m.title}</span>
                {m.building && <span className="muted small">{m.building.name}</span>}
              </Link>
              <blockquote className="mention-body">
                <MentionText body={m.body} />
              </blockquote>
            </li>
          );
        })}
      </ul>
    </>
  );
}
